import type { ComponentType, ReactNode, SVGProps } from "react";
import { useEffect, useRef } from "react";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import ArrowLeftLinear from "~icons/solar/arrow-left-linear";
import { IconButton } from "./IconButton";
import { cn } from "../../lib/utils";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl";

const SIZE: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Icon shown in the tinted chip beside the title. */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  description?: ReactNode;
  size?: ModalSize;
  /** Right-aligned action row; usually Cancel + primary Button. */
  footer?: ReactNode;
  /** Hide the close button (still closes on Escape and backdrop). */
  hideClose?: boolean;
  /** Show a back arrow before the title — for a modal stacked on another. */
  onBack?: () => void;
  /** Remove body padding for edge-to-edge content like tables. */
  flush?: boolean;
  /** Render no header — the body supplies its own (custom title rows). */
  hideHeader?: boolean;
  /** Let absolutely-positioned children (type-ahead lists) overflow the panel instead of scrolling the body. */
  overflowVisible?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * Native <dialog> modal in the v3 skin. Same open/close discipline as
 * ConfirmModal: showModal() when `open` turns true, close() only when it
 * turns false (never in effect cleanup — Strict Mode double-mount).
 */
export function Modal({
  open,
  onClose,
  title,
  icon: Icon,
  description,
  size = "md",
  footer,
  hideClose = false,
  onBack,
  flush = false,
  hideHeader = false,
  overflowVisible = false,
  children,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        dialog.showModal();
        // Land focus on the panel itself, not the first control (v3 shows no
        // focus ring on the close button when a modal opens).
        dialog.focus({ preventScroll: true });
      }
    } else if (dialog.open) {
      dialog.close();
    }

    const handleClose = () => onCloseRef.current();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [open]);

  // Escape with a menu open inside the dialog should close the menu, not the
  // dialog. The keydown (capture) still sees the menu in the DOM; the native
  // cancel that follows is then swallowed once.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    let swallowCancel = false;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        dialog.querySelector('[role="menu"], [role="listbox"]')
      ) {
        swallowCancel = true;
      }
    };
    const handleCancel = (e: Event) => {
      if (swallowCancel) {
        e.preventDefault();
        swallowCancel = false;
      }
    };
    dialog.addEventListener("keydown", handleKeyDown, true);
    dialog.addEventListener("cancel", handleCancel);
    return () => {
      dialog.removeEventListener("keydown", handleKeyDown, true);
      dialog.removeEventListener("cancel", handleCancel);
    };
  }, []);

  // Close on backdrop click only when the press started on the backdrop too,
  // so a drag out of the panel or a layout shift mid-click never closes it.
  const pressedOnBackdrop = useRef(false);
  const handleBackdropPointerDown = (e: React.PointerEvent<HTMLDialogElement>) => {
    pressedOnBackdrop.current = e.target === e.currentTarget;
  };
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget && pressedOnBackdrop.current) onClose();
    pressedOnBackdrop.current = false;
  };

  return (
    <dialog
      ref={dialogRef}
      tabIndex={-1}
      aria-label={typeof title === "string" ? title : undefined}
      onPointerDown={handleBackdropPointerDown}
      onClick={handleBackdropClick}
      className={cn(
        "fixed inset-0 m-auto h-fit max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] rounded-4xl outline-none border border-black/10 bg-white p-0 text-ink shadow-pop motion-safe:animate-modal-in backdrop:bg-ink/40 backdrop:backdrop-blur-sm backdrop:motion-safe:animate-modal-backdrop-in",
        overflowVisible ? "overflow-visible" : "overflow-hidden",
        SIZE[size],
        className
      )}
    >
      <div className="flex max-h-[calc(100vh-2rem)] flex-col">
        {/* Header */}
        {!hideHeader && (
        <div className="flex items-start justify-between gap-4 border-b border-black/[0.06] px-6 pb-3.5 pt-5">
          <div className="flex min-w-0 items-center gap-2.5">
            {onBack && (
              <IconButton
                icon={ArrowLeftLinear}
                aria-label="Back"
                variant="secondary"
                size="sm"
                onClick={onBack}
                className="shrink-0"
              />
            )}
            {Icon && (
              <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary">
                <Icon aria-hidden="true" className="size-4" />
              </span>
            )}
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-ink">{title}</h2>
              {description && (
                <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
              )}
            </div>
          </div>
          {!hideClose && (
            <IconButton
              icon={CloseCircleLinear}
              aria-label="Close"
              size="sm"
              onClick={onClose}
              className="-mr-1 -mt-1"
            />
          )}
        </div>
        )}

        {/* Body — omitted when there is nothing to show (confirm dialogs) */}
        {children != null && children !== false && (
          <div className={cn("min-h-0 flex-1", overflowVisible ? "overflow-visible" : "overflow-y-auto", !flush && "px-6 py-5")}>
            {children}
          </div>
        )}

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-black/[0.06] bg-surface-2 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
